'use client';

/**
 * authedFetch — `fetch` for signed-in API calls, with two jobs beyond fetching (#2700).
 *
 * 1. When a request comes back 401, CONFIRM whether the session is actually
 *    gone before believing it, then raise the session-lost signal so the user
 *    is told instead of staring at a page that silently stopped loading.
 * 2. Once the session is known gone, stop sending requests at all. Background
 *    polls previously kept firing every 60s forever against a dead session —
 *    on 2026-07-30 one gym's tab produced ~32 rejected requests per route with
 *    nobody able to see why.
 *
 * WHY THE CONFIRMATION STEP EXISTS — do not remove it to "simplify":
 * a 401 is not proof. Our own logs show 401s during ordinary refresh-token
 * rotation and during provider slowdowns, where the session recovers seconds
 * later. `supabase.auth.getSession()` reads local storage and only reports a
 * missing session when it is genuinely missing. If that call ERRORS (the
 * provider is unreachable), we deliberately treat the session as still alive:
 * showing "you've been signed out" during an outage would be a worse bug than
 * the one this fixes, because it would send people to re-authenticate against
 * a service that cannot authenticate them.
 */

import { supabase } from '@/utils/supabase/client';
import {
  currentSessionGeneration,
  isSessionLost,
  markSessionLost,
  noteSessionAlive,
} from '@/utils/auth/sessionLost';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('authedFetch');

/**
 * Thrown instead of performing a request once the session is confirmed gone.
 * Callers that already handle fetch failures need no change — they will show
 * their existing error state while the banner explains the real reason.
 */
export class SessionLostError extends Error {
  readonly isSessionLost = true;

  constructor() {
    super('Signed out');
    this.name = 'SessionLostError';
  }
}

export function isSessionLostError(err: unknown): err is SessionLostError {
  return (err as SessionLostError | null)?.isSessionLost === true;
}

/**
 * Ask Supabase whether a session is really PRESENT.
 *
 * The deliberate mirror image of `confirmSessionGone`, not its negation. Each
 * direction requires POSITIVE evidence and returns false when the check fails:
 *   - raising the banner needs proof the session is gone
 *   - lowering it needs proof the session is back
 * So an unreachable auth service neither raises a false alarm nor silently
 * clears a real one.
 */
export async function hasLiveSession(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) return false;
    return Boolean(data.session);
  } catch {
    return false;
  }
}

/**
 * Ask Supabase whether the session is really gone.
 *
 * Returns false (i.e. "not confirmed gone") when the check itself fails, which
 * is the safe direction: a network or provider failure must never be read as a
 * sign-out. Mirrors the re-check the mobile webview already does in
 * `utils/webview/sessionMonitor.ts` — same question, same answer, one behavior.
 */
export async function confirmSessionGone(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      logger.warn('Session re-check failed; assuming session is still alive', {
        message: error.message,
      });
      return false;
    }
    return !data.session;
  } catch (err) {
    logger.warn('Session re-check threw; assuming session is still alive', {
      message: (err as Error)?.message,
    });
    return false;
  }
}

/**
 * `fetch`, but it stops once the session is confirmed gone and raises the
 * signal that drives the "You've been signed out" banner.
 *
 * Drop-in for `fetch` on authenticated same-origin API calls. It does NOT
 * attach any credentials — cookies already ride along — so it is safe to swap
 * in without changing how a request is authorized.
 */
export async function authedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  if (isSessionLost()) {
    // RE-CONFIRM RATHER THAN LATCH (review finding on #2709).
    //
    // Clearing the flag only on auth EVENTS is structurally unable to notice
    // the recovery shape this app actually has. Supabase's storage here IS the
    // cookies written by middleware, and auth-js emits nothing when a session
    // appears in storage underneath the client — `_autoRefreshTokenTick` only
    // fires TOKEN_REFRESHED when the stored session is near expiry, so a
    // healthy restored session produces no event at all. Another tab signing
    // in is therefore invisible, which is exactly the flap seen in production
    // at 23:13 on 2026-07-30.
    //
    // COST: usually a local storage read, but NOT always free — when the
    // stored session is inside auth-js's expiry margin, `getSession()` falls
    // through to `_callRefreshToken`, which is a network call. Bounded in
    // practice: auth-js de-duplicates concurrent refreshes, and a success
    // clears the flag immediately so subsequent polls take the fast path.
    if (!(await hasLiveSession())) {
      throw new SessionLostError();
    }
    // noteSessionAlive, NOT clearSessionLost: this path must not wipe the
    // intentional-sign-out marker (see sessionLost.ts).
    noteSessionAlive();
  }

  const response = await fetch(input, init);

  if (response.status === 401) {
    const generation = currentSessionGeneration();
    if (await confirmSessionGone()) {
      logger.warn('401 confirmed as a lost session', {
        url: typeof input === 'string' ? input : String(input),
      });
      // Pass the generation: if the session came back while we were asking,
      // this answer is stale and must not raise a banner.
      markSessionLost(generation);
      if (isSessionLost()) {
        throw new SessionLostError();
      }
    }
    // Otherwise the session is alive and this 401 is something else (a slow
    // refresh, a route the user genuinely may not call). Hand the response
    // back unchanged so existing error handling is untouched.
    //
    // DELIBERATELY no `clearSessionLost()` on this path. It would also clear
    // the intentional sign-out marker, which could let the banner appear
    // during a deliberate sign-out. Recovery is handled at the TOP of this
    // function, where `hasLiveSession()` gates it on positive proof.
  }

  return response;
}
